import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);
const ORIGINAL_TOKEN_KEY = "original_access_token";
const VIEW_AS_EXP_KEY = "view_as_expires_at";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // `null` means "not yet loaded" — critical so gated routes (like /register)
  // don't fire a redirect before /public/settings resolves.
  const [siteSettings, setSiteSettings] = useState(null);
  const [impersonating, setImpersonating] = useState(() => !!localStorage.getItem(ORIGINAL_TOKEN_KEY));
  // Feature flags map (key → bool) + a loaded marker so FeatureGate can suspend rendering
  // until the network round-trip completes, avoiding a flash-of-redirect on refresh.
  const [featureFlags, setFeatureFlags] = useState({});
  const [featureFlagsLoaded, setFeatureFlagsLoaded] = useState(false);
  const refreshFeatureFlags = async () => {
    try {
      const { data } = await api.get("/public/feature-flags");
      setFeatureFlags(data?.flags || {});
    } catch (err) {
      console.warn("Failed to load feature flags:", err);
      setFeatureFlags({});
    } finally {
      setFeatureFlagsLoaded(true);
    }
  };

  useEffect(() => {
    api.get("/public/settings")
      .then(r => setSiteSettings(r.data || { self_registration_enabled: false }))
      .catch(() => setSiteSettings({ self_registration_enabled: false }));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (localStorage.getItem("access_token")) {
          // Legacy stored token → convert to cookie session once, then forget it.
          try { await api.post("/auth/refresh-cookie"); } catch { /* best effort */ }
          localStorage.removeItem("access_token");
        }
        setUser(data);
        setImpersonating(!!data.view_as);
        await refreshFeatureFlags();
      } catch {
        localStorage.removeItem("access_token");
        setFeatureFlagsLoaded(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.removeItem("access_token");
    localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    setImpersonating(false);
    setUser(data.user);
    await refreshFeatureFlags();
    return data.user;
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    await refreshFeatureFlags();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.warn("Logout API call failed:", err); }
    localStorage.removeItem("access_token");
    localStorage.removeItem(ORIGINAL_TOKEN_KEY);
    setImpersonating(false);
    setUser(null);
  };

  const impersonate = async (userId) => {
    const { data } = await api.post(`/admin/impersonate/${userId}`);
    localStorage.setItem(VIEW_AS_EXP_KEY, data.expires_at || "");
    setImpersonating(true);
    setUser({ ...data.user, view_as: { actor_id: data.impersonated_by, expires_at: data.expires_at } });
    await refreshFeatureFlags();
    return data.user;
  };

  const exitImpersonation = async () => {
    try { await api.post("/auth/view-as/end"); } catch { /* token may have expired — the audit end is best-effort */ }
    localStorage.removeItem(VIEW_AS_EXP_KEY);
    setImpersonating(false);
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await refreshFeatureFlags();
    } catch (err) {
      console.warn("Failed to restore admin session, forcing logout:", err);
      await logout();
    }
  };

  // Hard stop at the 30-minute mark even if the tab was left open.
  useEffect(() => {
    if (!impersonating) return undefined;
    const exp = localStorage.getItem(VIEW_AS_EXP_KEY);
    const ms = exp ? new Date(exp).getTime() - Date.now() : 0;
    const t = setTimeout(() => { exitImpersonation().then(() => { window.location.href = "/app/admin/users&viewas=expired"; }); }, Math.max(ms, 0));
    return () => clearTimeout(t);
  }, [impersonating]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo(
    () => ({ user, loading, impersonating, siteSettings, featureFlags, featureFlagsLoaded, refreshFeatureFlags, login, register, logout, impersonate, exitImpersonation, setUser }),
    [user, loading, impersonating, siteSettings, featureFlags, featureFlagsLoaded]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
